import { Injectable } from '@angular/core';
import { Observable, throwError, of, forkJoin } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import type { CryptoFees, CryptoWithdrawalFee, ExchangeFees, FiatFees, WithdrawalFee } from 'src/app/models/exchange-fees';
import { CurrenciesService } from './currencies.service';

@Injectable({
  providedIn: 'root'
})
export class FeesService {

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private currenciesService: CurrenciesService,
  ) { }

  getExchangeFees(): Observable<ExchangeFees> {
    const token = this.auth.getToken();

    return this.api.get(`{
      exchangeFees{
        tradeFees{
          pairs
          title
          steps{
            fromVolume{amount currency}
            toVolume{amount currency}
            makerFee
            takerFee
            makerFeeWithoutDiscount
            takerFeeWithoutDiscount
          }
          actualFeeStep{
            fromVolume{amount currency}
            toVolume{amount currency}
            makerFee
            takerFee
            makerFeeWithoutDiscount
            takerFeeWithoutDiscount
          }
          type
          feeDiscountPercent
          feeDiscountDateTo
        }
        withdrawalFees{
          currency
          amount{amount currency}
          minAmount{amount currency}
          fees{
            network{
              name
              code
              alias
            }
            feeAmount{amount currency}
            minAmount{amount currency}
          }
        }
        last30DaysUsdVolume{amount currency}
        last30DaysAedVolume{amount currency}
        last30DaysVolume{amount currency}
      }
    }`, null, token ? true : false).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['exchangeFees']);
      }),
      this.api.unifyErrors()
    );
  }

  getCryptoFees(): Observable<CryptoFees> {
    return forkJoin({
      fees: this.getExchangeFees(),
      crypto: this.currenciesService.getCryptoHash()
    }).pipe(
      map(({ fees, crypto }) => {
        const cryptoWithdrawlFees: CryptoWithdrawalFee[] = [];

        fees?.withdrawalFees?.forEach(item => {
          item?.fees?.forEach(fee => {
            cryptoWithdrawlFees.push({
              name: crypto[item.currency]?.name,
              currency: item.currency,
              ...fee
            });
          });
        })

        return {...fees, withdrawalFees: cryptoWithdrawlFees};
      })
    );
  }

  // TODO: fix mock
  getFiatFees(): Observable<FiatFees> {
    const token = this.auth.getToken();

    return this.api.get(`{
     fiatFees{
        withdrawalFees{
          currency
          amount
          flatAmount{amount currency}
          country
          fiatWithdrawalType
          supportedCurrencies
          additionalData{maxConnections}
        }
        depositFees{
          currency
          amount
          flatAmount{amount currency}
          country
          fiatDepositType
          supportedCurrencies
          additionalData{maxConnections}
        }
      }
    }`, null, !!token).pipe(
      switchMap(response => {
        if (response['errors']) {
          return throwError(response);
        }

        return of(response['data']['fiatFees']);
      }),
      this.api.unifyErrors()
    );
  }

  private _processCryptoWithdrawalFees(cryptoWithdrawlFees: WithdrawalFee) {

  }
}
